import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { jobListings } from '../data/mockData';
import { ChevronLeftIcon, MapPinIcon, BriefcaseIcon, ClockIcon, DollarSignIcon, CheckCircleIcon, BuildingIcon, UsersIcon, GlobeIcon, ShareIcon, BookmarkIcon } from 'lucide-react';
const JobDetail = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const job = jobListings.find(j => j.id === id);
  if (!job) {
    return <div className="min-h-screen bg-[#0F1115] flex items-center justify-center px-4 relative" data-id="element-1142">
        {/* Global Grain Texture */}
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none z-50 bg-grain mix-blend-overlay" data-id="element-1143"></div>

        <div className="text-center relative z-10" data-id="element-1144">
          <h1 className="text-2xl font-bold text-white mb-4" data-id="element-1145">Job Not Found</h1>
          <p className="text-gray-400 mb-6" data-id="element-1146">
            We couldn't find the job you're looking for.
          </p>
          <Link to="/jobs" data-id="element-1147">
            <button className="px-6 py-3 bg-[#d73b3e] text-white rounded-full font-semibold hover:bg-[#c02f32] transition-all duration-300" data-id="element-1148">
              Back to Jobs
            </button>
          </Link>
        </div>
      </div>;
  }
  // Format salary range
  const formatSalary = () => {
    const {
      min,
      max,
      currency
    } = job.salaryRange;
    if (min < 1000) {
      return `${currency}${min}-${max}/hr`;
    }
    const formatNumber = (num: number) => {
      if (num >= 1000000) {
        return `${(num / 1000000).toFixed(1)}M`;
      }
      return `${Math.round(num / 1000)}K`;
    };
    return `${currency}${formatNumber(min)}-${formatNumber(max)}`;
  };
  // Format date
  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
    }
    const months = Math.floor(diffDays / 30);
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  };
  return <div className="min-h-screen bg-[#0F1115] pb-20 relative" data-id="element-1149">
      {/* Global Grain Texture */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none z-50 bg-grain mix-blend-overlay" data-id="element-1150"></div>

      {/* Cover Image */}
      <div className="h-80 w-full bg-gradient-to-br from-gray-900 via-[#1a1d24] to-gray-900 relative overflow-hidden" data-id="element-1151">
        <div className="absolute inset-0 opacity-10" data-id="element-1152">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-[#d73b3e]/20 to-transparent rounded-full blur-3xl" data-id="element-1153" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-orange-500/20 to-transparent rounded-full blur-3xl" data-id="element-1154" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1115] to-transparent opacity-80" data-id="element-1155"></div>

        {/* Back Button */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-8 relative z-10" data-id="element-1156">
          <Link to="/jobs" className="inline-flex items-center text-gray-400 hover:text-white transition-colors" data-id="element-1157">
            <ChevronLeftIcon className="w-5 h-5 mr-1" data-id="element-1158" />
            Back to Jobs
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 -mt-40 relative z-10" data-id="element-1159">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1160">
          {/* Left Sidebar (Company Info & Apply) */}
          <div className="lg:col-span-1" data-id="element-1161">
            <div className="bg-[#1a1d24] rounded-2xl border border-gray-800 shadow-xl overflow-hidden sticky top-24" data-id="element-1162">
              <div className="p-6 text-center border-b border-gray-800" data-id="element-1163">
                <div className="w-24 h-24 bg-white/5 rounded-xl flex items-center justify-center overflow-hidden border border-gray-800 mx-auto mb-4" data-id="element-1164">
                  <img src={job.company.logo} alt={job.company.name} className="w-full h-full object-contain p-3" data-id="element-1165" />
                </div>

                <h2 className="text-xl font-bold text-white mb-1" data-id="element-1166">
                  {job.company.name}
                </h2>
                <p className="text-gray-400 text-sm mb-6" data-id="element-1167">
                  {job.company.location}
                </p>

                <a href={job.applicationUrl} target="_blank" rel="noopener noreferrer" className="block w-full px-6 py-3 bg-[#d73b3e] text-white rounded-full font-semibold hover:bg-[#c02f32] transition-all duration-300 shadow-lg mb-3" data-id="element-1168">
                  Apply Now
                </a>

                <div className="flex gap-2" data-id="element-1169">
                  <button className="flex-1 px-4 py-2.5 bg-white/10 backdrop-blur-sm text-white rounded-full text-sm font-medium hover:bg-white/20 transition-all duration-300 border border-white/20 flex items-center justify-center gap-2" data-id="element-1170">
                    <BookmarkIcon className="w-4 h-4" data-id="element-1171" />
                    Save
                  </button>
                  <button className="flex-1 px-4 py-2.5 bg-white/10 backdrop-blur-sm text-white rounded-full text-sm font-medium hover:bg-white/20 transition-all duration-300 border border-white/20 flex items-center justify-center gap-2" data-id="element-1172">
                    <ShareIcon className="w-4 h-4" data-id="element-1173" />
                    Share
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6" data-id="element-1174">
                {/* Job Details */}
                <div data-id="element-1175">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-1176">
                    Job Details
                  </h3>
                  <div className="space-y-3" data-id="element-1177">
                    <div className="flex items-start gap-3" data-id="element-1178">
                      <MapPinIcon className="w-5 h-5 text-[#d73b3e] mt-0.5 flex-shrink-0" data-id="element-1179" />
                      <div data-id="element-1180">
                        <div className="text-sm text-gray-400" data-id="element-1181">Location</div>
                        <div className="text-white font-medium" data-id="element-1182">
                          {job.location}
                        </div>
                        <span className="inline-block mt-1 px-2 py-0.5 bg-white/5 rounded-full text-xs capitalize border border-gray-800" data-id="element-1183">
                          {job.locationType}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-start gap-3" data-id="element-1184">
                      <BriefcaseIcon className="w-5 h-5 text-[#d73b3e] mt-0.5 flex-shrink-0" data-id="element-1185" />
                      <div data-id="element-1186">
                        <div className="text-sm text-gray-400" data-id="element-1187">
                          Employment Type
                        </div>
                        <div className="text-white font-medium capitalize" data-id="element-1188">
                          {job.employmentType.replace('-', ' ')}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3" data-id="element-1189">
                      <UsersIcon className="w-5 h-5 text-[#d73b3e] mt-0.5 flex-shrink-0" data-id="element-1190" />
                      <div data-id="element-1191">
                        <div className="text-sm text-gray-400" data-id="element-1192">
                          Experience Level
                        </div>
                        <div className="text-white font-medium capitalize" data-id="element-1193">
                          {job.experienceLevel === 'entry' ? 'Entry Level' : job.experienceLevel === 'mid' ? 'Mid Level' : job.experienceLevel === 'senior' ? 'Senior Level' : 'Lead / Manager'}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3" data-id="element-1194">
                      <DollarSignIcon className="w-5 h-5 text-[#d73b3e] mt-0.5 flex-shrink-0" data-id="element-1195" />
                      <div data-id="element-1196">
                        <div className="text-sm text-gray-400" data-id="element-1197">
                          Salary Range
                        </div>
                        <div className="text-white font-medium" data-id="element-1198">
                          {formatSalary()}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3" data-id="element-1199">
                      <ClockIcon className="w-5 h-5 text-[#d73b3e] mt-0.5 flex-shrink-0" data-id="element-1200" />
                      <div data-id="element-1201">
                        <div className="text-sm text-gray-400" data-id="element-1202">Posted</div>
                        <div className="text-white font-medium" data-id="element-1203">
                          {formatRelativeDate(job.postedDate)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Required Skills */}
                <div data-id="element-1204">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3" data-id="element-1205">
                    Required Skills
                  </h3>
                  <div className="flex flex-wrap gap-2" data-id="element-1206">
                    {job.skills.map(skill => <span key={skill} className="px-3 py-1.5 bg-[#d73b3e]/10 text-[#d73b3e] rounded-full text-xs border border-[#d73b3e]/20 font-medium" data-id="element-1207">
                        {skill}
                      </span>)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content (Job Description) */}
          <div className="lg:col-span-2 space-y-8" data-id="element-1208">
            {/* Job Header */}
            <div className="bg-[#1a1d24] rounded-2xl border border-gray-800 p-8 shadow-lg" data-id="element-1209">
              {job.featured && <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#d73b3e]/10 backdrop-blur-sm rounded-full border border-[#d73b3e]/20 mb-4" data-id="element-1210">
                  <span className="text-sm font-medium text-[#d73b3e]" data-id="element-1211">
                    ⭐ Featured Job
                  </span>
                </div>}
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1212">
                {job.title}
              </h1>
              <p className="text-lg text-gray-300 leading-relaxed" data-id="element-1213">
                {job.description}
              </p>
            </div>

            {/* Responsibilities */}
            <div className="bg-[#1a1d24] rounded-2xl border border-gray-800 p-8 shadow-lg" data-id="element-1214">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-1215">
                <CheckCircleIcon className="w-6 h-6 text-[#d73b3e]" data-id="element-1216" />
                Responsibilities
              </h2>
              <ul className="space-y-3" data-id="element-1217">
                {job.responsibilities.map((responsibility, index) => <li key={index} className="flex items-start gap-3" data-id="element-1218">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#d73b3e] mt-2 flex-shrink-0" data-id="element-1219"></div>
                    <span className="text-gray-300 leading-relaxed" data-id="element-1220">
                      {responsibility}
                    </span>
                  </li>)}
              </ul>
            </div>

            {/* Requirements */}
            <div className="bg-[#1a1d24] rounded-2xl border border-gray-800 p-8 shadow-lg" data-id="element-1221">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-1222">
                <CheckCircleIcon className="w-6 h-6 text-[#d73b3e]" data-id="element-1223" />
                Requirements
              </h2>
              <ul className="space-y-3" data-id="element-1224">
                {job.requirements.map((requirement, index) => <li key={index} className="flex items-start gap-3" data-id="element-1225">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#d73b3e] mt-2 flex-shrink-0" data-id="element-1226"></div>
                    <span className="text-gray-300 leading-relaxed" data-id="element-1227">
                      {requirement}
                    </span>
                  </li>)}
              </ul>
            </div>

            {/* About Company */}
            <div className="bg-[#1a1d24] rounded-2xl border border-gray-800 p-8 shadow-lg" data-id="element-1228">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-1229">
                <BuildingIcon className="w-6 h-6 text-[#d73b3e]" data-id="element-1230" />
                About {job.company.name}
              </h2>
              <p className="text-gray-300 leading-relaxed mb-6" data-id="element-1231">
                {job.company.name} is a leading technology company focused on
                building innovative solutions. We're passionate about creating
                products that make a difference and fostering a culture of
                collaboration and growth.
              </p>
              <div className="grid sm:grid-cols-2 gap-4" data-id="element-1232">
                <div className="bg-[#0F1115] p-4 rounded-xl border border-gray-800" data-id="element-1233">
                  <div className="text-gray-400 text-sm mb-1" data-id="element-1234">Company Size</div>
                  <div className="text-xl font-bold text-white" data-id="element-1235">
                    50-200 employees
                  </div>
                </div>
                <div className="bg-[#0F1115] p-4 rounded-xl border border-gray-800" data-id="element-1236">
                  <div className="text-gray-400 text-sm mb-1" data-id="element-1237">Industry</div>
                  <div className="text-xl font-bold text-white" data-id="element-1238">Technology</div>
                </div>
              </div>
            </div>

            {/* Apply CTA */}
            <div className="bg-gradient-to-br from-[#d73b3e]/10 to-orange-500/10 rounded-2xl border border-[#d73b3e]/20 p-8 text-center" data-id="element-1239">
              <h3 className="text-2xl font-bold text-white mb-3" data-id="element-1240">
                Ready to apply?
              </h3>
              <p className="text-gray-300 mb-6 max-w-xl mx-auto" data-id="element-1241">
                Join {job.company.name} and be part of an innovative team
                building the future of technology.
              </p>
              <a href={job.applicationUrl} target="_blank" rel="noopener noreferrer" className="inline-block px-8 py-4 bg-[#d73b3e] text-white rounded-full font-semibold text-lg hover:bg-[#c02f32] transition-all duration-300 shadow-lg hover:shadow-xl" data-id="element-1242">
                Apply for this Position
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default JobDetail;